import { useState } from "react";
import { CloseIcon } from "../../../common/functions";

let positionInfoFields = ["id", "BB Ticker", "ISIN", "Location", "Currency", "Notional Amount", "Average Cost", "Coupon Rate", "Coupon Frequency", "Maturity", "Issuer", "Asset Class"];

function CorporateAction(props: any) {
  let [redemptionPrice, setRedemptionPrice] = useState("100");
  let [accruedInterest, setAccruedInterest] = useState("");
  let [actionType, setActionType] = useState("maturity_redemption");
  let [newCouponRate, setNewCouponRate] = useState("0");
  let [writeDownPrice, setWriteDownPrice] = useState("");
  let [redeemAmount, setRedeemAmount] = useState("");
  let [partialPrice, setPartialPrice] = useState("100");
  let [partialAccrued, setPartialAccrued] = useState("");
  let [currentFactor, setCurrentFactor] = useState("");
  let [newFactor, setNewFactor] = useState("");
  let [cashCoupon, setCashCoupon] = useState("");
  let [pikCoupon, setPikCoupon] = useState("");
  let [pikFrequency, setPikFrequency] = useState(props.position["Coupon Frequency"] ? props.position["Coupon Frequency"].toString() : "4");
  let [tenderedAmount, setTenderedAmount] = useState("");
  let [costBasisTreatment, setCostBasisTreatment] = useState("carry_over");
  let [outturns, setOutturns] = useState<any[]>([]);

  if (!props.position["BB Ticker"]) {
    return <div></div>;
  }

  let notional = parseFloat(props.position["Notional Amount"]) || 0;
  let averageCost = parseFloat(props.position["Average Cost"]) || 0;
  let currentCoupon = parseFloat(props.position["Coupon Rate"]) || 0;
  let currentMid = parseFloat(props.position["Mid"]) || 0;
  let mtdMark = parseFloat(props.position["MTD Mark"]) || 0;
  let mtdInt = parseFloat(props.position["MTD Int."]) || 0;
  let cdsFactor = parseFloat(props.position["CDS Factor"]) || 1;
  let isCDS = props.position["Type"] == "CDS";
  // Bonds, USTs and CDS all quote prices in points (price/100 = decimal),
  // so they share the same principal / MTD-rlzd derivation here. The legacy
  // else-branch (which multiplied by raw price) was only correct for futures
  // / equity-style instruments and produced wrong corporate-action numbers
  // for CDS. CDS picks up an extra factor so partial-redemption-style write
  // downs and similar match the cdsMTM sign convention used elsewhere.
  let isBond = props.position["Type"] == "BND" || props.position["Type"] == "UST" || isCDS;
  let isRedemption = actionType == "maturity_redemption" || actionType == "early_call";
  let isDefault = actionType == "bond_default";
  let isPartial = actionType == "partial_redemption";
  let isPik = actionType == "pik_coupon_payment";
  let isRestructure = actionType == "debt_restructuring";

  let tenderedNum = parseFloat(tenderedAmount) || 0;
  let totalNewFace = outturns.reduce((s, o) => s + (parseFloat(o.newNotional) || 0), 0);
  let pxDivider = isBond ? 100 : 1;
  let avgCostDecimal = averageCost;
  let totalOldCost = avgCostDecimal * tenderedNum;
  let restructureRows = outturns.map((o) => {
    let nn = parseFloat(o.newNotional) || 0;
    let allocCost = totalNewFace > 0 ? totalOldCost * (nn / totalNewFace) : 0;
    let day1MidDec = (parseFloat(o.day1Mid) || 0) / pxDivider;
    let newAvgCostDec = costBasisTreatment == "carry_over" ? (nn > 0 ? allocCost / nn : 0) : day1MidDec;
    let midDec = day1MidDec || newAvgCostDec;
    let day1URlzd = nn * (midDec - newAvgCostDec);
    return { ...o, nn, allocCost, newAvgCost: newAvgCostDec * pxDivider, mid: midDec * pxDivider, day1URlzd };
  });
  let sumNewCost = restructureRows.reduce((s, r) => s + r.nn * (r.newAvgCost / pxDivider), 0);
  let weightedSellPx = totalNewFace > 0 ? restructureRows.reduce((s, r) => s + r.nn * r.mid, 0) / totalNewFace : averageCost * pxDivider;
  let realizedPnL = costBasisTreatment == "realized" ? tenderedNum * ((weightedSellPx / pxDivider) - averageCost) : 0;
  let costPreservationDrift = Math.abs(totalOldCost - sumNewCost);

  let updateOutturn = (idx: number, field: string, val: string) => {
    let copy = outturns.slice();
    copy[idx] = { ...copy[idx], [field]: val };
    setOutturns(copy);
  };
  let addOutturn = () => setOutturns([...outturns, { bbTicker: "", isin: "", bloombergId: "", newNotional: "", couponRate: "", maturity: "", day1Mid: "", type: "BND", currency: props.position["Currency"] || "USD" }]);
  let removeOutturn = (idx: number) => setOutturns(outturns.filter((_, i) => i != idx));

  let price = parseFloat(redemptionPrice) || 0;
  let accruedAmt = parseFloat(accruedInterest) || 0;
  let avgCostDisplay = isBond ? (averageCost * 100).toFixed(4) : averageCost.toFixed(4);
  let principalCashFlow = isBond ? Math.abs(notional) * cdsFactor * price / 100 : Math.abs(notional) * price;
  let totalCashFlow = principalCashFlow + accruedAmt;

  let mtdMarkDisplay = isBond ? (mtdMark * 100).toFixed(4) : mtdMark.toFixed(4);
  let mtdRlzd = isBond ? notional * cdsFactor * (price / 100 - mtdMark) : notional * (price - mtdMark);

  let newCoupon = parseFloat(newCouponRate) || 0;
  let writeDown = parseFloat(writeDownPrice) || 0;
  let currentMidDisplay = isBond ? (currentMid * 100).toFixed(4) : currentMid.toFixed(4);
  let writeDownImpact = 0;
  if (writeDown > 0 && isBond) {
    writeDownImpact = notional * cdsFactor * (writeDown / 100 - currentMid);
  } else if (writeDown > 0) {
    writeDownImpact = notional * (writeDown - currentMid);
  }

  let partialAmt = parseFloat(redeemAmount) || 0;
  let partialPriceVal = parseFloat(partialPrice) || 100;
  let partialAccruedAmt = parseFloat(partialAccrued) || 0;
  let partialRemainingNotional = notional - partialAmt;
  let partialPrincipal = isBond ? partialAmt * cdsFactor * partialPriceVal / 100 : partialAmt * partialPriceVal;
  let partialTotalCashFlow = partialPrincipal + partialAccruedAmt;
  let partialMtdRlzd = isBond ? partialAmt * cdsFactor * (partialPriceVal / 100 - mtdMark) : partialAmt * (partialPriceVal - mtdMark);

  let notionalAfterSink = isRestructure ? notional + (notional >= 0 ? -writeDown : writeDown) : notional;
  let notionalAfterPartial = isRestructure ? notionalAfterSink - (notionalAfterSink >= 0 ? partialAmt : -partialAmt) : notional;

  let cashCouponVal = parseFloat(cashCoupon) || 0;
  let pikCouponVal = parseFloat(pikCoupon) || 0;
  let pikFreqVal = parseFloat(pikFrequency) || 4;
  let pikTotalCoupon = cashCouponVal + pikCouponVal;
  let pikIncrease = pikCouponVal > 0 ? Math.abs(notional) * (pikCouponVal / 100) / pikFreqVal : 0;
  let pikNewNotional = notional + pikIncrease;
  let pikCashPayment = cashCouponVal > 0 ? Math.abs(notional) * (cashCouponVal / 100) / pikFreqVal : 0;

  let formatNumber = (value: any) => {
    let num = parseFloat(value);
    if (isNaN(num)) return String(value || "");
    return num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  let getDisplayValue = (field: string, value: any) => {
    if (field === "Notional Amount") return formatNumber(value);
    if (field === "Average Cost") return avgCostDisplay;
    if (field === "Coupon Rate") return (parseFloat(value) || 0).toFixed(4);
    return String(value !== undefined && value !== null ? value : "");
  };

  let handleFactorChange = (updatedCurrent: string, updatedNew: string) => {
    let oldF = parseFloat(updatedCurrent);
    let newF = parseFloat(updatedNew);
    if (!isNaN(oldF) && !isNaN(newF) && oldF > 0 && newF >= 0 && oldF > newF) {
      let computed = Math.round(notional * (oldF - newF) / oldF);
      setRedeemAmount(computed.toString());
    }
  };

  let headerTitle = isRestructure ? "Debt Restructuring: " + props.position["BB Ticker"] : isPik ? "PIK Coupon Payment: " + props.position["BB Ticker"] : isDefault ? "Bond Default: " + props.position["BB Ticker"] : isPartial ? "Partial Redemption: " + props.position["BB Ticker"] : "Redemption: " + props.position["BB Ticker"];
  let submitLabel = isRestructure ? "Confirm" : isPik ? "Confirm PIK Coupon Payment" : isDefault ? "Confirm Bond Default" : isPartial ? "Confirm Partial Redemption" : "Confirm Redemption";

  return (
    <div className="edit-position-modal">
      <div className="edit-position-header">
        <h3 className="edit-position-title">{headerTitle}</h3>
        <div onClick={(event) => props.onCancel(event)} className="close-icon-container-edit-position">
          <CloseIcon />
        </div>
      </div>

      <form id="corporate-action-form" onSubmit={(event) => props.onSubmit(event)}>
        <input type="hidden" name="id" value={props.position["id"]} />

        <div className="edit-position-grid">
          <div className="edit-section">
            <div className="edit-section-title">Position Details</div>
            <div className="edit-section-fields">
              {positionInfoFields.map((field) => {
                let value = props.position[field];
                return (
                  <div className="edit-field-row" key={field}>
                    <label className="edit-field-label">{field}</label>
                    <input type="text" className="edit-position-input" value={getDisplayValue(field, value)} disabled />
                  </div>
                );
              })}
            </div>
          </div>

          <div className="edit-section">
            <div className="edit-section-title">Action</div>
            <div className="edit-section-fields">
              <div className="edit-field-row">
                <label className="edit-field-label">Action Type</label>
                <select name="actionType" className="edit-position-input" value={actionType} onChange={(e) => setActionType(e.target.value)}>
                  <option value="maturity_redemption">Maturity Redemption</option>
                  <option value="early_call">Early Call</option>
                  <option value="partial_redemption">Partial Redemption</option>
                  <option value="bond_default">Bond Default</option>
                  <option value="pik_coupon_payment">PIK Coupon Payment</option>
                  <option value="debt_restructuring">Debt Restructuring</option>
                </select>
              </div>

              {isRedemption && (
                <>
                  <div className="edit-field-row">
                    <label className="edit-field-label">Redemption Price</label>
                    <input type="text" name="redemptionPrice" className="edit-position-input" value={redemptionPrice} onChange={(e) => setRedemptionPrice(e.target.value)} required />
                  </div>
                  <div className="edit-field-row">
                    <label className="edit-field-label">Accrued Interest ({props.position["Currency"] || "USD"})</label>
                    <input type="text" name="accruedInterest" className="edit-position-input" value={accruedInterest} onChange={(e) => setAccruedInterest(e.target.value)} placeholder="From broker statement (enter 0 if none)" required />
                  </div>
                  <div className="edit-field-row">
                    <label className="edit-field-label">Settlement Date</label>
                    <input type="date" name="settlementDate" className="edit-position-input" required />
                  </div>
                </>
              )}

              {isDefault && (
                <>
                  <div className="edit-field-row">
                    <label className="edit-field-label">New Coupon Rate (%)</label>
                    <input type="text" name="newCouponRate" className="edit-position-input" value={newCouponRate} onChange={(e) => setNewCouponRate(e.target.value)} required />
                  </div>
                  <div className="edit-field-row">
                    <label className="edit-field-label">Write Down Price</label>
                    <input type="text" name="writeDownPrice" className="edit-position-input" value={writeDownPrice} onChange={(e) => setWriteDownPrice(e.target.value)} placeholder="Optional" />
                  </div>
                  <div className="edit-field-row">
                    <label className="edit-field-label">Effective Date</label>
                    <input type="date" name="settlementDate" className="edit-position-input" required />
                  </div>
                </>
              )}

              {isPartial && (
                <>
                  <div className="edit-field-row">
                    <label className="edit-field-label">Current Pool Factor</label>
                    <input type="text" className="edit-position-input" value={currentFactor} onChange={(e) => { setCurrentFactor(e.target.value); handleFactorChange(e.target.value, newFactor); }} placeholder="e.g. 0.73 (optional)" />
                  </div>
                  <div className="edit-field-row">
                    <label className="edit-field-label">New Pool Factor</label>
                    <input type="text" className="edit-position-input" value={newFactor} onChange={(e) => { setNewFactor(e.target.value); handleFactorChange(currentFactor, e.target.value); }} placeholder="e.g. 0.53 (optional)" />
                  </div>
                  <div className="edit-field-row">
                    <label className="edit-field-label">Redemption Amount</label>
                    <input type="text" name="redeemAmount" className="edit-position-input" value={redeemAmount} onChange={(e) => setRedeemAmount(e.target.value)} placeholder="Notional being redeemed" required />
                  </div>
                  <div className="edit-field-row">
                    <label className="edit-field-label">Redemption Price</label>
                    <input type="text" name="redemptionPrice" className="edit-position-input" value={partialPrice} onChange={(e) => setPartialPrice(e.target.value)} required />
                  </div>
                  <div className="edit-field-row">
                    <label className="edit-field-label">Accrued Interest ({props.position["Currency"] || "USD"})</label>
                    <input type="text" name="accruedInterest" className="edit-position-input" value={partialAccrued} onChange={(e) => setPartialAccrued(e.target.value)} placeholder="From broker statement (enter 0 if none)" required />
                  </div>
                  <div className="edit-field-row">
                    <label className="edit-field-label">Settlement Date</label>
                    <input type="date" name="settlementDate" className="edit-position-input" required />
                  </div>
                </>
              )}

              {isRestructure && (
                <>
                  <input type="hidden" name="originalNotional" value={notional} />
                  <input type="hidden" name="writeDownPrice" value="0" />
                  <input type="hidden" name="outturns" value={JSON.stringify(outturns.map(o => ({ ...o, day1Mid: (parseFloat(o.day1Mid) || 0) / pxDivider })))} />
                  <input type="hidden" name="costBasisTreatment" value={costBasisTreatment} />
                  <div className="edit-field-row">
                    <label className="edit-field-label">Effective Date</label>
                    <input type="date" name="settlementDate" className="edit-position-input" required />
                  </div>
                  <div className="edit-field-row">
                    <label className="edit-field-label">Pool Factor (current)</label>
                    <input type="text" className="edit-position-input" value={currentFactor} onChange={(e) => { setCurrentFactor(e.target.value); handleFactorChange(e.target.value, newFactor); }} placeholder="e.g. 0.73" />
                  </div>
                  <div className="edit-field-row">
                    <label className="edit-field-label">Pool Factor (new)</label>
                    <input type="text" className="edit-position-input" value={newFactor} onChange={(e) => { setNewFactor(e.target.value); handleFactorChange(currentFactor, e.target.value); }} placeholder="e.g. 0.53" />
                  </div>
                  <div className="edit-field-row">
                    <label className="edit-field-label">Amount Redeemed</label>
                    <input type="text" name="redeemAmount" className="edit-position-input" value={redeemAmount} onChange={(e) => setRedeemAmount(e.target.value)} placeholder="Auto-filled from pool factors" />
                  </div>
                  <div className="edit-field-row">
                    <label className="edit-field-label">Price</label>
                    <input type="text" name="redemptionPrice" className="edit-position-input" value={partialPrice} onChange={(e) => setPartialPrice(e.target.value)} />
                  </div>
                  <div className="edit-field-row">
                    <label className="edit-field-label">Accrued Interest ({props.position["Currency"] || "USD"})</label>
                    <input type="text" name="accruedInterest" className="edit-position-input" value={partialAccrued} onChange={(e) => setPartialAccrued(e.target.value)} placeholder="0" />
                  </div>
                  {outturns.length > 0 && (
                    <>
                      <div className="edit-section-title" style={{ marginTop: "12px" }}>Bond Exchange</div>
                      <div className="edit-field-row">
                        <label className="edit-field-label">Cost Basis</label>
                        <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                          <label style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "12px" }}>
                            <input type="radio" name="costBasisTreatment" value="carry_over" checked={costBasisTreatment == "carry_over"} onChange={(e) => setCostBasisTreatment(e.target.value)} />
                            Carry Over
                          </label>
                          <label style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "12px" }}>
                            <input type="radio" name="costBasisTreatment" value="realized" checked={costBasisTreatment == "realized"} onChange={(e) => setCostBasisTreatment(e.target.value)} />
                            Realized
                          </label>
                        </div>
                      </div>
                      <div className="edit-field-row">
                        <label className="edit-field-label">Tendered Amount</label>
                        <input type="text" name="tenderedAmount" className="edit-position-input" value={tenderedAmount} onChange={(e) => setTenderedAmount(e.target.value)} placeholder={"Max " + formatNumber(notionalAfterPartial)} required />
                      </div>
                    </>
                  )}
                  {outturns.map((o, idx) => (
                    <div key={idx} style={{ border: "1px solid #ddd", borderRadius: "4px", padding: "8px", marginBottom: "6px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
                        <strong style={{ fontSize: "12px" }}>New Bond #{idx + 1}</strong>
                        <button type="button" onClick={() => removeOutturn(idx)} style={{ background: "transparent", border: "1px solid #c00", color: "#c00", padding: "2px 8px", borderRadius: "3px", fontSize: "11px", cursor: "pointer" }}>Remove</button>
                      </div>
                      <div className="edit-field-row">
                        <label className="edit-field-label">BB Ticker</label>
                        <input type="text" className="edit-position-input" value={o.bbTicker} onChange={(e) => updateOutturn(idx, "bbTicker", e.target.value)} required />
                      </div>
                      <div className="edit-field-row">
                        <label className="edit-field-label">ISIN</label>
                        <input type="text" className="edit-position-input" value={o.isin} onChange={(e) => updateOutturn(idx, "isin", e.target.value)} required />
                      </div>
                      <div className="edit-field-row">
                        <label className="edit-field-label">Notional</label>
                        <input type="text" className="edit-position-input" value={o.newNotional} onChange={(e) => updateOutturn(idx, "newNotional", e.target.value)} placeholder="Face amount" required />
                      </div>
                      <div className="edit-field-row">
                        <label className="edit-field-label">Coupon (%)</label>
                        <input type="text" className="edit-position-input" value={o.couponRate} onChange={(e) => updateOutturn(idx, "couponRate", e.target.value)} placeholder="0" />
                      </div>
                      <div className="edit-field-row">
                        <label className="edit-field-label">Maturity</label>
                        <input type="text" className="edit-position-input" value={o.maturity} onChange={(e) => updateOutturn(idx, "maturity", e.target.value)} placeholder="DD/MM/YY" />
                      </div>
                      <div className="edit-field-row">
                        <label className="edit-field-label">Day-1 Mid</label>
                        <input type="text" className="edit-position-input" value={o.day1Mid} onChange={(e) => updateOutturn(idx, "day1Mid", e.target.value)} required />
                      </div>
                      <div className="edit-field-row">
                        <label className="edit-field-label">Type / Currency</label>
                        <div style={{ display: "flex", gap: "4px" }}>
                          <select className="edit-position-input" value={o.type} onChange={(e) => updateOutturn(idx, "type", e.target.value)} style={{ flex: 1 }}>
                            <option value="BND">BND</option>
                            <option value="UST">UST</option>
                            <option value="EQT">EQT</option>
                          </select>
                          <input type="text" className="edit-position-input" value={o.currency} onChange={(e) => updateOutturn(idx, "currency", e.target.value)} placeholder="USD" style={{ flex: 1 }} />
                        </div>
                      </div>
                    </div>
                  ))}
                  <button type="button" onClick={addOutturn} style={{ background: "#f0f0f0", border: "1px dashed #888", padding: "6px 12px", borderRadius: "3px", fontSize: "12px", cursor: "pointer", marginBottom: "8px", width: "100%" }}>+ Add Bond Exchange</button>
                </>
              )}

              {isPik && (
                <>
                  <div className="edit-field-row">
                    <label className="edit-field-label">Cash Coupon (%)</label>
                    <input type="text" name="cashCoupon" className="edit-position-input" value={cashCoupon} onChange={(e) => setCashCoupon(e.target.value)} placeholder="e.g. 5 for 5%" required />
                  </div>
                  <div className="edit-field-row">
                    <label className="edit-field-label">PIK Coupon (%)</label>
                    <input type="text" name="pikCoupon" className="edit-position-input" value={pikCoupon} onChange={(e) => setPikCoupon(e.target.value)} placeholder="e.g. 3 for 3%" required />
                  </div>
                  <div className="edit-field-row">
                    <label className="edit-field-label">Coupon Frequency (per year)</label>
                    <input type="text" name="couponFrequency" className="edit-position-input" value={pikFrequency} onChange={(e) => setPikFrequency(e.target.value)} required />
                  </div>
                  <div className="edit-field-row">
                    <label className="edit-field-label">Starting Effective Date</label>
                    <input type="date" name="settlementDate" className="edit-position-input" required />
                  </div>
                </>
              )}

              <div className="edit-field-row">
                <label className="edit-field-label">Note</label>
                <input type="text" name="note" className="edit-position-input" placeholder="Enter note (required)" required />
              </div>
            </div>
          </div>

          {isRedemption && (
            <div className="edit-section">
              <div className="edit-section-title">Settlement Summary</div>
              <div className="edit-section-fields">
                <div className="edit-field-row">
                  <label className="edit-field-label">Notional</label>
                  <input type="text" className="edit-position-input" value={formatNumber(notional)} disabled />
                </div>
                <div className="edit-field-row">
                  <label className="edit-field-label">Redemption Price</label>
                  <input type="text" className="edit-position-input" value={price} disabled />
                </div>
                <div className="edit-field-row">
                  <label className="edit-field-label">Principal Cash Flow</label>
                  <input type="text" className="edit-position-input" value={formatNumber(principalCashFlow)} disabled />
                </div>
                <div className="edit-field-row">
                  <label className="edit-field-label">AI Paid by Issuer</label>
                  <input type="text" className="edit-position-input" value={formatNumber(accruedAmt)} disabled />
                </div>
                <div className="edit-field-row">
                  <label className="edit-field-label">Total Cash Flow</label>
                  <input type="text" className="edit-position-input" value={formatNumber(totalCashFlow)} disabled />
                </div>
                <div className="edit-field-row">
                  <label className="edit-field-label">MTD AI (P&L)</label>
                  <input type="text" className="edit-position-input" value={formatNumber(mtdInt)} disabled />
                </div>
                <div className="edit-field-row">
                  <label className="edit-field-label">Last Month Price</label>
                  <input type="text" className="edit-position-input" value={mtdMarkDisplay} disabled />
                </div>
                <div className="edit-field-row">
                  <label className="edit-field-label">MTD Rlzd Gain/Loss</label>
                  <input type="text" className="edit-position-input" value={formatNumber(mtdRlzd)} disabled />
                </div>
              </div>
            </div>
          )}

          {isDefault && (
            <div className="edit-section">
              <div className="edit-section-title">Default Impact</div>
              <div className="edit-section-fields">
                <div className="edit-field-row">
                  <label className="edit-field-label">Notional (unchanged)</label>
                  <input type="text" className="edit-position-input" value={formatNumber(notional)} disabled />
                </div>
                <div className="edit-field-row">
                  <label className="edit-field-label">Coupon Rate Change</label>
                  <input type="text" className="edit-position-input" value={currentCoupon.toFixed(4) + " -> " + newCoupon.toFixed(4)} disabled />
                </div>
                <div className="edit-field-row">
                  <label className="edit-field-label">Interest Accrual</label>
                  <input type="text" className="edit-position-input" value={newCoupon == 0 ? "Stopped (zero coupon)" : "Reduced to " + newCoupon.toFixed(2) + "%"} disabled />
                </div>
                {writeDown > 0 && (
                  <>
                    <div className="edit-field-row">
                      <label className="edit-field-label">Current Mid Price</label>
                      <input type="text" className="edit-position-input" value={currentMidDisplay} disabled />
                    </div>
                    <div className="edit-field-row">
                      <label className="edit-field-label">Write Down To</label>
                      <input type="text" className="edit-position-input" value={writeDownPrice} disabled />
                    </div>
                    <div className="edit-field-row">
                      <label className="edit-field-label">Writedown Impact</label>
                      <input type="text" className="edit-position-input" value={formatNumber(writeDownImpact)} disabled />
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {isPartial && (
            <div className="edit-section">
              <div className="edit-section-title">Partial Redemption Summary</div>
              <div className="edit-section-fields">
                {currentFactor && newFactor && (
                  <div className="edit-field-row">
                    <label className="edit-field-label">Pool Factor</label>
                    <input type="text" className="edit-position-input" value={currentFactor + " -> " + newFactor} disabled />
                  </div>
                )}
                <div className="edit-field-row">
                  <label className="edit-field-label">Current Notional</label>
                  <input type="text" className="edit-position-input" value={formatNumber(notional)} disabled />
                </div>
                <div className="edit-field-row">
                  <label className="edit-field-label">Redeemed Amount</label>
                  <input type="text" className="edit-position-input" value={formatNumber(partialAmt)} disabled />
                </div>
                <div className="edit-field-row">
                  <label className="edit-field-label">Remaining Notional</label>
                  <input type="text" className="edit-position-input" value={formatNumber(partialRemainingNotional)} disabled />
                </div>
                <div className="edit-field-row">
                  <label className="edit-field-label">Redemption Price</label>
                  <input type="text" className="edit-position-input" value={partialPriceVal} disabled />
                </div>
                <div className="edit-field-row">
                  <label className="edit-field-label">Principal Cash Flow</label>
                  <input type="text" className="edit-position-input" value={formatNumber(partialPrincipal)} disabled />
                </div>
                <div className="edit-field-row">
                  <label className="edit-field-label">AI Paid by Issuer</label>
                  <input type="text" className="edit-position-input" value={formatNumber(partialAccruedAmt)} disabled />
                </div>
                <div className="edit-field-row">
                  <label className="edit-field-label">Total Cash Flow</label>
                  <input type="text" className="edit-position-input" value={formatNumber(partialTotalCashFlow)} disabled />
                </div>
                <div className="edit-field-row">
                  <label className="edit-field-label">MTD AI (P&L)</label>
                  <input type="text" className="edit-position-input" value={formatNumber(mtdInt)} disabled />
                </div>
                <div className="edit-field-row">
                  <label className="edit-field-label">Last Month Price</label>
                  <input type="text" className="edit-position-input" value={mtdMarkDisplay} disabled />
                </div>
                <div className="edit-field-row">
                  <label className="edit-field-label">MTD Rlzd Gain/Loss</label>
                  <input type="text" className="edit-position-input" value={formatNumber(partialMtdRlzd)} disabled />
                </div>
              </div>
            </div>
          )}

          {isPik && (
            <div className="edit-section">
              <div className="edit-section-title">PIK Coupon Summary</div>
              <div className="edit-section-fields">
                <div className="edit-field-row">
                  <label className="edit-field-label">Current Notional</label>
                  <input type="text" className="edit-position-input" value={formatNumber(notional)} disabled />
                </div>
                <div className="edit-field-row">
                  <label className="edit-field-label">Previous Coupon Rate</label>
                  <input type="text" className="edit-position-input" value={currentCoupon.toFixed(4) + "%"} disabled />
                </div>
                <div className="edit-field-row">
                  <label className="edit-field-label">Cash Coupon</label>
                  <input type="text" className="edit-position-input" value={cashCouponVal.toFixed(4) + "%"} disabled />
                </div>
                <div className="edit-field-row">
                  <label className="edit-field-label">PIK Coupon</label>
                  <input type="text" className="edit-position-input" value={pikCouponVal.toFixed(4) + "%"} disabled />
                </div>
                <div className="edit-field-row">
                  <label className="edit-field-label">Total Coupon (Cash + PIK)</label>
                  <input type="text" className="edit-position-input" value={pikTotalCoupon.toFixed(4) + "%"} disabled />
                </div>
                <div className="edit-field-row">
                  <label className="edit-field-label">Frequency</label>
                  <input type="text" className="edit-position-input" value={pikFreqVal + "x per year"} disabled />
                </div>
                <div className="edit-field-row" style={{ borderTop: "1px solid #ddd", paddingTop: "8px", marginTop: "4px" }}>
                  <label className="edit-field-label" style={{ fontWeight: "bold" }}>PIK Notional Increase</label>
                  <input type="text" className="edit-position-input" style={{ fontWeight: "bold" }} value={formatNumber(pikIncrease)} disabled />
                </div>
                <div className="edit-field-row">
                  <label className="edit-field-label" style={{ fontWeight: "bold" }}>New Notional</label>
                  <input type="text" className="edit-position-input" style={{ fontWeight: "bold" }} value={formatNumber(pikNewNotional)} disabled />
                </div>
                <div className="edit-field-row" style={{ borderTop: "1px solid #ddd", paddingTop: "8px", marginTop: "4px" }}>
                  <label className="edit-field-label">Expected Cash Coupon Payment</label>
                  <input type="text" className="edit-position-input" value={formatNumber(pikCashPayment)} disabled />
                </div>
              </div>
            </div>
          )}

          {isRestructure && (
            <div className="edit-section">
              <div className="edit-section-title">Summary</div>
              <div className="edit-section-fields">
                <div className="edit-field-row">
                  <label className="edit-field-label">Current Notional</label>
                  <input type="text" className="edit-position-input" value={formatNumber(notional)} disabled />
                </div>
                {currentFactor && newFactor && (
                  <div className="edit-field-row">
                    <label className="edit-field-label">Pool Factor</label>
                    <input type="text" className="edit-position-input" value={currentFactor + " \u2192 " + newFactor} disabled />
                  </div>
                )}
                {partialAmt > 0 && (
                  <>
                    <div className="edit-field-row">
                      <label className="edit-field-label">Amount Redeemed</label>
                      <input type="text" className="edit-position-input" value={formatNumber(partialAmt) + " @ " + partialPriceVal} disabled />
                    </div>
                    <div className="edit-field-row">
                      <label className="edit-field-label">Principal</label>
                      <input type="text" className="edit-position-input" value={formatNumber(partialPrincipal)} disabled />
                    </div>
                    {partialAccruedAmt > 0 && (
                      <div className="edit-field-row">
                        <label className="edit-field-label">Accrued Interest</label>
                        <input type="text" className="edit-position-input" value={formatNumber(partialAccruedAmt)} disabled />
                      </div>
                    )}
                    <div className="edit-field-row" style={{ fontWeight: "bold" }}>
                      <label className="edit-field-label" style={{ fontWeight: "bold" }}>Total Proceeds</label>
                      <input type="text" className="edit-position-input" style={{ fontWeight: "bold" }} value={formatNumber(partialTotalCashFlow)} disabled />
                    </div>
                  </>
                )}
                {outturns.length > 0 && (
                  <>
                    <div className="edit-field-row" style={{ borderTop: "1px solid #ddd", paddingTop: "8px", marginTop: "4px" }}>
                      <label className="edit-field-label">Tendered</label>
                      <input type="text" className="edit-position-input" value={formatNumber(tenderedNum)} disabled />
                    </div>
                    <div className="edit-field-row">
                      <label className="edit-field-label">New Face (total)</label>
                      <input type="text" className="edit-position-input" value={formatNumber(totalNewFace)} disabled />
                    </div>
                    <div className="edit-field-row">
                      <label className="edit-field-label" style={{ fontWeight: "bold" }}>Cost Drift</label>
                      <input type="text" className="edit-position-input" style={{ fontWeight: "bold", color: costPreservationDrift < 1 ? "green" : "red" }} value={costPreservationDrift.toFixed(2) + (costPreservationDrift < 1 ? " OK" : " MISMATCH")} disabled />
                    </div>
                    {restructureRows.map((r, idx) => (
                      <div key={idx} style={{ fontSize: "11px", padding: "4px 0", borderBottom: "1px dotted #eee" }}>
                        <div><strong>{r.bbTicker || "Bond #" + (idx + 1)}</strong>: {formatNumber(r.nn)} @ {r.newAvgCost.toFixed(4)}</div>
                      </div>
                    ))}
                  </>
                )}
                <div className="edit-field-row" style={{ borderTop: "1px solid #ddd", paddingTop: "8px", marginTop: "4px" }}>
                  <label className="edit-field-label" style={{ fontWeight: "bold" }}>New Notional</label>
                  <input type="text" className="edit-position-input" style={{ fontWeight: "bold" }} value={formatNumber(notionalAfterPartial - (outturns.length > 0 ? (notionalAfterPartial >= 0 ? tenderedNum : -tenderedNum) : 0))} disabled />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="edit-position-footer">
          <button type="button" className="btn btn-cancel" onClick={(event) => props.onCancel(event)}>
            Cancel
          </button>
          <button type="submit" className="btn upload-btn">
            {submitLabel}
          </button>
        </div>
      </form>

      {props.authStatus && <div className="edit-position-error">{props.authStatus}</div>}
    </div>
  );
}

export default CorporateAction;
